import { EXPORT_FPS } from '@/lib/timeline/exportFrameTiming';
import { applyCropFramingToAll } from '@/lib/crop/applyCropFramingToAll';
import { applySceneCropFraming, countSceneCropSections, removeSceneCropSection } from '@/lib/crop/sceneCropFraming';
import React, { useMemo, useState } from "react";
import { Crop } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import CropCenterEditor from "@/components/CropCenterEditor";
import { TransitionBadge } from "@/components/transitions/TransitionBadge";
import {
  keyframeBoundaries,
  KEYFRAME_TRANSITION_EPS,
} from "@/lib/transitions/keyframeCuts";
import {
  TRANSFORM_TRANSITION_KINDS,
  type KeyframeTransition,
  type SceneTransition,
  type TransitionKind,
} from "@/lib/transitions/types";
import type { CropCenterKeyframe } from "@/hooks/use-auto-save-crop-keyframes";

interface Props {
  compact?: boolean;
  controlsBelow?: boolean;
  keyframes: CropCenterKeyframe[];
  intervalStart: number;
  intervalEnd: number;
  pixelsPerSecond: number;
  viewStart: number;
  viewEnd: number;
  currentTime: number;
  videoUrl?: string;
  sourceAspectRatio?: number;
  onChange?: (next: CropCenterKeyframe[]) => void;
  onSeekRequest?: (time: number) => void;
  /** Per-boundary transitions (transform family only). */
  keyframeTransitions?: KeyframeTransition[];
  onKeyframeTransitionChange?: (atTime: number, transition: SceneTransition | null) => void;
  onApplyKeyframeTransitionToAll?: (transition: SceneTransition) => void;
  onRandomizeKeyframeTransitions?: (kinds: TransitionKind[]) => void;
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const CropCenterTrack: React.FC<Props> = ({
  keyframes,
  compact = false,
  controlsBelow = false,
  intervalStart,
  intervalEnd,
  pixelsPerSecond,
  viewStart,
  viewEnd,
  currentTime,
  videoUrl,
  sourceAspectRatio,
  onChange,
  onSeekRequest,
  keyframeTransitions,
  onKeyframeTransitionChange,
  onApplyKeyframeTransitionToAll,
  onRandomizeKeyframeTransitions,
}) => {
  const { t } = useTranslation();
  // Track the open editor by where its section starts inside the scene. The
  // first edit splits the shot keyframe at the scene edge, so the keyframe's
  // own start time changes; the section start does not.
  const [openKey, setOpenKey] = useState<number | null>(null);

  // Boundaries between consecutive keyframes — the same midpoints
  // keyframeBoundaries derives for the cut lib, so a badge maps 1:1 to a cut.
  const boundaries = useMemo(() => keyframeBoundaries(keyframes), [keyframes]);

  const intervalDuration = intervalEnd - intervalStart;
  const visibleSegments = useMemo(() => {
    if (intervalEnd <= intervalStart || pixelsPerSecond <= 0) return [];
    return keyframes.flatMap((kf, idx) => {
      const start = Math.max(kf.time, intervalStart);
      const end = Math.min(kf.endTime, intervalEnd);
      const visibleStart = Math.max(start, viewStart);
      const visibleEnd = Math.min(end, viewEnd);
      if (visibleEnd <= visibleStart) return [];
      return [{ kf, idx, start, end, visibleStart, visibleEnd }];
    });
  }, [keyframes, intervalStart, intervalEnd, pixelsPerSecond, viewStart, viewEnd]);

  const controlSegments = controlsBelow && visibleSegments.length > 1
    ? [visibleSegments.find(segment => currentTime >= segment.start && currentTime < segment.end) ?? visibleSegments[0]]
    : visibleSegments;

  const activeIndex = useMemo(() => {
    for (let i = 0; i < keyframes.length; i++) {
      const kf = keyframes[i];
      if (currentTime >= kf.time && currentTime <= kf.endTime) return i;
    }
    return -1;
  }, [keyframes, currentTime]);

  const handleChangeOne = (start: number, end: number, next: CropCenterKeyframe) => {
    if (!onChange) return;
    onChange(applySceneCropFraming({
      keyframes,
      scenes: [{ startTime: intervalStart, endTime: intervalEnd }],
      range: { start, end },
      framing: { x: next.x, y: next.y, zoom: next.zoom },
    }));
  };

  // Deleting merges a section into its neighbor inside this scene, so it
  // needs a second section here; it never reaches the shot's other scenes.
  const scene = { startTime: intervalStart, endTime: intervalEnd };
  const canDeleteSection = countSceneCropSections(keyframes, scene) > 1;
  const handleDelete = (start: number, end: number) => {
    if (!onChange || !canDeleteSection) return;
    onChange(removeSceneCropSection({ keyframes, scenes: [scene], scene, range: { start, end } }));
    setOpenKey(null);
  };

  if (!keyframes.length || intervalDuration <= 0) return null;

  // Stop React synthetic events from bubbling to the parent Timeline, which
  // otherwise treats clicks/drags as seeks or interval manipulation. Radix
  // popovers render through a portal but React events still bubble up the
  // virtual tree, so we have to guard the content boundary too.
  const stopBubble = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="interval-crop-controls">
      {controlSegments.map(({ kf, idx, start, end, visibleStart, visibleEnd }) => {
        const leftPercent = ((start - intervalStart) / intervalDuration) * 100;
        // Keep crop triggers clear of the interval trim handles. Very short
        // sections still have a keyboard-focusable target, without widening.
        const edgeInset = Math.min(12 / pixelsPerSecond, intervalDuration / 4);
        const buttonStart = Math.max(visibleStart, intervalStart + edgeInset);
        const buttonEnd = Math.min(visibleEnd, intervalEnd - edgeInset);
        const controlStart = buttonEnd > buttonStart ? buttonStart : visibleStart;
        const controlEnd = buttonEnd > buttonStart ? buttonEnd : visibleEnd;
        const selectedTime = currentTime >= start && currentTime < end ? currentTime : start;
        // The editor uses 30 fps. Skip the cut frame, but stay inside short scenes.
        const referenceTime = selectedTime < start + 1 / EXPORT_FPS
          ? Math.min(start + 5 / EXPORT_FPS, end - Math.min(1 / EXPORT_FPS, (end - start) / 2))
          : selectedTime;
        const isActive = idx === activeIndex;
        const isOpen = openKey === start;
        const editLabel = t("cropCenter.editPosition", { time: formatTime(start) });

        return (
          <Popover
            key={`cc-${start}`}
            open={isOpen}
            onOpenChange={(v) => {
              setOpenKey(v ? start : null);
              if (v) onSeekRequest?.(referenceTime);
            }}
          >
            {start > intervalStart && (
              <span
                aria-hidden="true"
                className="absolute inset-y-0 border-l border-dashed border-white/60"
                style={{ left: `${leftPercent}%` }}
              />
            )}
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`absolute ${controlsBelow ? "top-[calc(100%+6px)] z-20" : "bottom-1"} h-6 flex items-center justify-center rounded-sm hover:bg-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${compact ? "pointer-events-none group-hover/scene:pointer-events-auto group-focus-within/scene:pointer-events-auto focus-visible:pointer-events-auto data-[state=open]:pointer-events-auto opacity-0 group-hover/scene:opacity-100 group-focus-within/scene:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100" : "pointer-events-auto overflow-hidden"}`}
                style={{
                  left: controlsBelow ? "calc(50% - 26px)" : `${((controlStart - intervalStart) / intervalDuration) * 100}%`,
                  width: controlsBelow ? 24 : `${((controlEnd - controlStart) / intervalDuration) * 100}%`,
                }}
                title={`${editLabel} · ${formatTime(start)}–${formatTime(end)}`}
                aria-label={editLabel}
                onClick={stopBubble}
                onMouseDown={stopBubble}
                onPointerDown={stopBubble}
              >
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded bg-black/75 text-white ${
                  isOpen || isActive ? "ring-1 ring-inset ring-primary" : ""
                }`}>
                  <Crop className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              className="w-auto p-3"
              sideOffset={8}
              onClick={stopBubble}
              onMouseDown={stopBubble}
              onPointerDown={stopBubble}
              onPointerMove={stopBubble}
              onPointerUp={stopBubble}
            >
              <CropCenterEditor
                keyframe={kf}
                frameTime={referenceTime}
                previewStartTime={start}
                previewEndTime={end}
                videoUrl={videoUrl}
                sourceAspectRatio={sourceAspectRatio}
                canDelete={canDeleteSection}
                onChange={(next) => handleChangeOne(start, end, next)}
                onDelete={() => handleDelete(start, end)}
                onApplyToAllScenes={keyframes.length > 1 && onChange
                  ? (framing) => onChange(applyCropFramingToAll(keyframes, framing))
                  : undefined}
              />
            </PopoverContent>
          </Popover>
        );
      })}

      {/* Per-boundary transition badges (transform family only) */}
      {onKeyframeTransitionChange &&
        boundaries.map((boundary, idx) => {
          // Keyframe cuts only affect the interior of kept video intervals.
          // Edge cuts belong to the interval transition control instead.
          if (boundary <= intervalStart || boundary >= intervalEnd || boundary < viewStart || boundary > viewEnd) return null;
          const leftPercent = ((boundary - intervalStart) / intervalDuration) * 100;
          const current =
            keyframeTransitions?.find(
              (kt) => Math.abs(kt.atTime - boundary) < KEYFRAME_TRANSITION_EPS,
            ) ?? null;
          const asScene: SceneTransition | null = current
            ? {
                afterSceneIndex: idx,
                kind: current.kind,
                durationSec: current.durationSec,
                ...(current.direction ? { direction: current.direction } : {}),
              }
            : null;
          return (
            <div
              key={`kf-cut-${idx}`}
              className="group/cut pointer-events-auto absolute z-40"
              data-testid={`crop-transition-${boundary}`}
              style={{ left: `${leftPercent}%`, top: 20 }}
              onPointerDown={stopBubble}
              onMouseDown={stopBubble}
              onClick={stopBubble}
            >
              <TransitionBadge
                cutIndex={idx}
                transition={asScene}
                kinds={TRANSFORM_TRANSITION_KINDS}
                alwaysVisible={!compact}
                compact={compact}
                onChange={(tr) => onKeyframeTransitionChange(boundary, tr)}
                onApplyToAll={(tr) => onApplyKeyframeTransitionToAll?.(tr)}
                onRandomize={(kinds) => onRandomizeKeyframeTransitions?.(kinds)}
              />
            </div>
          );
        })}
    </div>
  );
};

export default CropCenterTrack;
